import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation, Link as RouterLink, useSearchParams } from 'react-router-dom';
// material-ui
import { styled, useTheme } from '@mui/material/styles';
import {
  AppBar,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  CssBaseline,
  Toolbar,
  Typography,
  useMediaQuery
} from '@mui/material';

// project imports
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import Header from './Header';
import Sidebar from './Sidebar';
import navigation from 'menu-items';
import { drawerWidth } from 'store/constant';
import { SET_MENU } from 'store/actions';

// assets
import { IconChevronRight } from '@tabler/icons';
import { Home } from '@mui/icons-material';
import MerchantRouteWatcher from 'layout/MerchantRouteWatcher';

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  ...theme.typography.mainContent,
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  transition: theme.transitions.create(
    'margin',
    open
      ? {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen
      }
      : {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
      }
  ),
  [theme.breakpoints.up('md')]: {
    marginLeft: open ? 0 : -(drawerWidth - 20),
    width: `calc(100% - ${drawerWidth}px)`
  },
  [theme.breakpoints.down('md')]: {
    marginLeft: '20px',
    width: `calc(100% - ${drawerWidth}px)`,
    padding: '16px'
  },
  [theme.breakpoints.down('sm')]: {
    marginLeft: '10px',
    width: `calc(100% - ${drawerWidth}px)`,
    padding: '16px',
    marginRight: '10px'
  }
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
  const theme = useTheme();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));

  const leftDrawerOpened = useSelector((state) => state.customization.opened);
  const dispatch = useDispatch();

  const handleLeftDrawerToggle = () => {
    dispatch({ type: SET_MENU, opened: !leftDrawerOpened });
  };


  useEffect(() => {
    const locationPathname = window.location.pathname.split('/');
    if (locationPathname.indexOf('addon') < 0) {
      localStorage.removeItem('filter_add_ons');
    }

    const isMerchantListPage = /^\/[^/]+\/merchants$/.test(location.pathname);

    if (isMerchantListPage) {
      localStorage.removeItem('merchantWizardPosition');
    }
  }, [location.pathname]);

  const forcedBreadcrumbs = useMemo(() => {
    const pathname = location.pathname;
    const parts = pathname.split('/').filter(Boolean);
    const tenant = parts[0];

    const merchantIdFromPath = parts[3];
    const merchantIdFromQuery = searchParams.get('merchantId') || searchParams.get('partnerId');

    if (/^\/[^/]+\/merchants\/admins\/add$/.test(pathname)) {
      return [
        { label: 'Setting', to: `/${tenant}/merchants` },
        {
          label: 'Court',
          to: merchantIdFromQuery ? `/${tenant}/merchants/edit/${merchantIdFromQuery}` : `/${tenant}/merchants`
        },
        { label: 'Admin' }
      ];
    }

    if (/^\/[^/]+\/merchants\/admins\/edit(\/[^/]+)?$/.test(pathname)) {
      return [
        { label: 'Setting', to: `/${tenant}/merchants` },
        {
          label: 'Court',
          to: merchantIdFromQuery ? `/${tenant}/merchants/edit/${merchantIdFromQuery}` : `/${tenant}/merchants`
        },
        { label: 'Admin' }
      ];
    }

    if (/^\/[^/]+\/merchants\/edit\/[^/]+$/.test(pathname)) {
      return [
        { label: 'Setting', to: `/${tenant}/merchants` },
        { label: 'Court' }
      ];
    }

    return null;
  }, [location.pathname, searchParams]);

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <MerchantRouteWatcher />
      <AppBar
        enableColorOnDark
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          bgcolor: theme.palette.background.default,
          transition: leftDrawerOpened ? theme.transitions.create('width') : 'none'
        }}
      >
        <Toolbar>
          <Header handleLeftDrawerToggle={handleLeftDrawerToggle} />
        </Toolbar>
      </AppBar>

      <Sidebar
        drawerOpen={!matchDownMd ? leftDrawerOpened : !leftDrawerOpened}
        drawerToggle={handleLeftDrawerToggle}
      />

      <Main theme={theme} open={leftDrawerOpened}>
        {forcedBreadcrumbs ? (
          <Box sx={{ mb: 1, mt: 1 }}>
            <MuiBreadcrumbs
              separator={
                <IconChevronRight
                  size={16}
                  stroke={1.8}
                  color={theme.palette.grey[500]}
                />
              }
              aria-label="breadcrumb"
            >
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: '#42A5F5',
                  textDecoration: 'none',
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color: '#1E88E5'
                  }
                }}
              >
                <Home sx={{ fontSize: 16 }} />
              </Box>

              {forcedBreadcrumbs.map((item, index) => {
                const isLast = index === forcedBreadcrumbs.length - 1;

                if (item.to && !isLast) {
                  return (
                    <Box
                      key={`${item.label}-${index}`}
                      component={RouterLink}
                      to={item.to}
                      sx={{
                        fontSize: '0.9rem',
                        lineHeight: 1.2,
                        fontWeight: 500,
                        color: theme.palette.grey[900],
                        textDecoration: 'none',
                        transition: 'color 0.2s ease',
                        '&:hover': {
                          color: theme.palette.primary.main,
                          // textDecoration: 'underline'
                        }
                      }}
                    >
                      {item.label}
                    </Box>
                  );
                }

                return (
                  <Typography
                    key={`${item.label}-${index}`}
                    sx={{
                      fontSize: '0.9rem',
                      lineHeight: 1.2,
                      fontWeight: 500,
                      color: isLast ? theme.palette.grey[500] : theme.palette.grey[900],
                      letterSpacing: 0
                    }}
                  >
                    {item.label}
                  </Typography>
                );
              })}
            </MuiBreadcrumbs>
          </Box>
        ) : (
          <Breadcrumbs
            separator={IconChevronRight}
            navigation={navigation}
            card={false}
            title={false}
            icon
            rightAlign={false}
          />
        )}
        <Outlet />
      </Main>
    </Box>
  );
};

export default MainLayout;